import cx from 'classnames'
import React, { useContext, useEffect, useState } from 'react'

import { QuizContext } from '@f/applications/QuizApp/context/QuizContext'
import { composeTileOptions } from '@f/applications/QuizApp/partials/composeTileOptions'
import { InputProcessingLoader } from '@f/applications/QuizApp/partials/InputProcessingLoader'
import QuestionHeading from '@f/applications/QuizApp/partials/QuestionHeading'
import { MID_STEP_LOADER_APPEARANCE_TIME } from '@f/applications/QuizApp/partials/quizConstants'
import { QuizStep } from '@f/applications/QuizApp/partials/types'
import { useBudgetSubmitted } from '@f/applications/QuizApp/questions/QuestionBudget/useBudgetSubmitted'
import {
  composeQualityBasedBudgetRadioOptions,
  getLivingAreaOneSizeSmallerRange,
  getLivingAreaRadioOptionByValue,
  QualityValue
} from '@f/applications/QuizApp/questions/QuestionBudget/utils'
import { QuestionRadioButton } from '@f/applications/QuizApp/questions/QuestionRadio/QuestionRadioButton'
import CTA from '@f/elements/CTA/CTA'

import { ExpansionStageValue } from '@f-fertighaus/applications/Next/utils/backendStructureMappings'
import { $TSFixMe } from '@f-fertighaus/applications/UserAccount/interfaces/utils'

interface Props {
  step: QuizStep
}

function getLivingAreaTotal(steps: QuizStep[]) {
  return steps.find(({ id }: { id: string }) => id === 'living_area_total')
    ?.values[0].value
}

function getSelectedExpansionStage(steps: QuizStep[]) {
  return steps.find(({ id }: { id: string }) => id === 'expansion_stage')
    ?.values[0].value
}

export const QuestionBudget = ({ step }: Props) => {
  const {
    actions: { updateValues, move },
    data: { steps },
    tracking,
    tileStyle
  } = useContext(QuizContext) as $TSFixMe
  const [isInputProcessing, setIsInputProcessing] = useState(false)
  const { budgetSubmitted, handleBudgetChange } = useBudgetSubmitted(step)

  const customValuesForQualityBasedValues: Record<
    ExpansionStageValue,
    QualityValue
  > = step.customConfig || {}

  const composeTiles = composeTileOptions(tileStyle)

  const living_area_total = Number(
    getLivingAreaRadioOptionByValue(getLivingAreaTotal(steps))?.inputProps
      ?.dataset?.budgetCalcValue
  )

  const selected_expansion_stage = getSelectedExpansionStage(steps)

  const expansion_stage =
    selected_expansion_stage === 'na' ? 'keyready' : selected_expansion_stage

  const handleLivingAreaChange = (value: number) => {
    if (!isNaN(value)) {
      updateValues({
        stepID: 'living_area_total',
        valuesToSave: [
          {
            name: 'living_area_total',
            value
          }
        ]
      })
    }
  }

  const handleClick = (value: number, id: string) => {
    window._wt({
      category: 'form',
      action: 'submit',
      location: `question-${step._wtName}`,
      values: [
        {
          type: 'quality',
          value: id
        }
      ],
      ...tracking.flow
    })
    handleBudgetChange(value)
  }

  const getCurrentRenderOptions = () => {
    return composeTiles(
      composeQualityBasedBudgetRadioOptions({
        expansion_stage,
        living_area_total,
        customValuesForQualityBasedValues
      })
    )
  }

  useEffect(() => {
    if (budgetSubmitted) {
      move({ to: 'next', triggerSubmit: true })
    }
  }, [budgetSubmitted])

  const oneSizeSmallerRange = getLivingAreaOneSizeSmallerRange(
    living_area_total
  )

  const handleLivingAreaToSmallerRangeChange = () => {
    setIsInputProcessing(true)
    setTimeout(() => {
      handleLivingAreaChange(oneSizeSmallerRange.value as number)
      setIsInputProcessing(false)
    }, MID_STEP_LOADER_APPEARANCE_TIME)
  }

  return (
    <div>
      {isInputProcessing && <InputProcessingLoader />}
      <QuestionHeading
        heading={step.heading}
        subheading='Unsere Empfehlungen für Ihr Projekt auf der Grundlage Ihrer Kriterien'
      />
      <div
        className={cx('[ f-m-h-auto ] QuestionRadio-wrap', {
          'has-more-than-four-elements': getCurrentRenderOptions().length > 4,
          'has-four-elements': getCurrentRenderOptions().length === 4
        })}>
        <div className='[ f-d-flc ] QuestionRadio-options'>
          <div
            className={cx(
              'QuestionBudget',
              tileStyle === 'card'
                ? 'QuestionRadio-card-tiles'
                : 'QuestionRadio-bar-tiles'
            )}>
            {getCurrentRenderOptions().map(({ ItemTemplate, ...option }) => (
              <QuestionRadioButton
                key={option.value}
                wtData={{
                  location: `question-${step._wtName}`,
                  ...tracking.flow
                }}
                onClick={
                  option.onClick
                    ? option.onClick
                    : () => handleClick(option.value, option.inputProps.id)
                }>
                <ItemTemplate {...option} />
              </QuestionRadioButton>
            ))}
          </div>
        </div>
      </div>
      {oneSizeSmallerRange && (
        <CTA
          type='text-primary'
          onClick={handleLivingAreaToSmallerRangeChange}
          className='[ f-t-body f-m-t-040 f-p-h-020 ] QuestionRadio-supplementary-button'>
          {`Budget zu hoch? Jetzt mit ${oneSizeSmallerRange.label} Wohnfläche neu berechnen`}
        </CTA>
      )}
    </div>
  )
}
